import React, { useState } from "react";
import { Shell, Section } from "@/components/loomi/Layout";
import { PM_NAV } from "./PmDashboard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CopyField } from "@/components/loomi/CopyField";
import QRCode from "react-qr-code";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Info, CheckCircle2, Send } from "lucide-react";

/**
 * "Redirect-first" project creation.
 *  Step 1: PM enters project meta WITHOUT a survey URL → redirect URLs generated → shared to client
 *  Step 2 (later): PM enters the survey URL client returned → project auto-transitions to "live"
 */
export default function PmCreate() {
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [created, setCreated] = useState(null);
  const [form, setForm] = useState({
    type: "adhoc", name: "", survey_url: "", client_id: "",
    target_n: 100, cpi: 0.5, loi: 5, incidence_rate: 80, reward_points: 30, is_public: true,
  });
  const [saving, setSaving] = useState(false);
  const [surveyUrl, setSurveyUrl] = useState("");
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/clients");
      setClients(data);
      if (data[0]) setForm(f => ({ ...f, client_id: data[0].id }));
    })();
  }, []);

  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/projects", form);
      const draft = data.status === "draft";
      toast.success(draft ? "Draft created — share redirects with your client" : "Ad-hoc project created & live");
      setCreated(data);
    } catch (err) {
      toast.error("Failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  const linkSurvey = async () => {
    if (!surveyUrl.trim()) return toast.error("Paste the survey link from your client first");
    setLinking(true);
    try {
      const { data } = await api.patch(`/projects/${created.id}/survey-url`, { survey_url: surveyUrl.trim() });
      setCreated(data);
      toast.success("Survey URL saved — project is now live");
    } catch (err) {
      toast.error("Failed", { description: formatApiError(err) });
    } finally {
      setLinking(false);
    }
  };

  // ─────────────── SUCCESS / DRAFT SCREEN ───────────────
  if (created) {
    const isDraft = created.status === "draft";
    const urls = created.urls || {};
    return (
      <Shell title={isDraft ? "Share these with your client" : "Project created"} portalLabel="PM" nav={PM_NAV}>
        {isDraft && (
          <div className="mb-6 rounded-2xl p-5 bg-slate-500/10 border border-slate-500/30 flex items-start gap-3" data-testid="draft-banner">
            <Info className="w-5 h-5 text-slate-300 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-slate-200 leading-relaxed">
              <b>This project is a draft.</b> Send the redirect URLs below to your client so they can configure their survey platform (Decipher / Qualtrics / etc.).
              Once they return the survey link, paste it in the box below and we'll flip the project to <b>live</b>.
            </div>
          </div>
        )}

        <Section title={created.name} testId="section-created"
          action={
            <button onClick={() => nav(`/pm/projects/${created.id}`)}
              className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-2"
              data-testid="go-to-created-btn">
              Open project →
            </button>
          }>
          <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Entry redirect (share this)</div>
                <CopyField value={urls.entry} testId="created-entry-url" />
              </div>
              {urls.complete && (
                <>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Complete callback</div>
                    <CopyField value={urls.complete} testId="created-complete-url" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Terminate callback</div>
                    <CopyField value={urls.terminate} testId="created-terminate-url" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Over-quota callback</div>
                    <CopyField value={urls.overquota} testId="created-overquota-url" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-mute mb-1">Security-term callback</div>
                    <CopyField value={urls.security_term} testId="created-security-url" />
                  </div>
                </>
              )}
            </div>
            <div className="flex flex-col items-center gap-3">
              <div className="bg-white p-4 rounded-lg" data-testid="created-qr">
                <QRCode value={urls.entry || "https://synclineresearch.com"} size={160} />
              </div>
              <div className="text-xs text-mute">QR of the entry link — for offline distribution or shared docs</div>
            </div>
          </div>
        </Section>

        {isDraft && (
          <Section title="Step 2 — Add survey URL when client returns their link" testId="section-add-survey-url">
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
              <input
                data-testid="pm-created-survey-url"
                value={surveyUrl}
                onChange={(e) => setSurveyUrl(e.target.value)}
                placeholder="https://survey.decipherinc.com/survey/…"
                className="flex-1 bg-black/50 border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <button
                onClick={linkSurvey}
                disabled={linking}
                data-testid="pm-created-survey-url-submit"
                className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 rounded-md px-5 py-2.5 text-sm font-semibold text-black flex items-center justify-center gap-2 shadow-[0_0_28px_rgba(16,185,129,0.35)]"
              >
                <Send className="w-4 h-4" />
                {linking ? "Saving…" : "Set survey URL & go live"}
              </button>
            </div>
            <p className="text-xs text-mute mt-3">Only the redirect URLs above (entry / complete / terminate / overquota / security_term) need to be configured on the client's survey platform. The survey URL you paste here is where SyncLine sends respondents once they hit the entry redirect.</p>
          </Section>
        )}

        {!isDraft && (
          <div className="mt-6 flex items-center gap-2 text-emerald-300 text-sm" data-testid="live-confirmation">
            <CheckCircle2 className="w-4 h-4" /> Survey URL set — the entry link is fully live.
          </div>
        )}

        <div className="mt-6 flex gap-2">
          <button onClick={() => { setCreated(null); setSurveyUrl(""); }}
            className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-2"
            data-testid="create-another-btn">
            Create another
          </button>
          <button onClick={() => nav("/pm")}
            className="text-xs bg-indigo-500 hover:bg-indigo-400 rounded-md px-3 py-2"
            data-testid="back-to-overview-btn">
            Back to overview
          </button>
        </div>
      </Shell>
    );
  }

  // ─────────────── CREATION FORM ───────────────
  return (
    <Shell title="Create ad-hoc project" portalLabel="PM" nav={PM_NAV}>
      <form onSubmit={submit} className="glass rounded-2xl p-8 max-w-2xl mx-auto" data-testid="pm-create-form">
        <div className="grid grid-cols-2 gap-4">
          <F label="Project name" wide>
            <input required data-testid="pm-create-name" value={form.name} onChange={set("name")} className="in" />
          </F>
          <F label="Client">
            <select value={form.client_id} onChange={set("client_id")} data-testid="pm-create-client" className="in">
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>
          <F label="Visibility">
            <select value={form.is_public ? "public" : "targeted"}
              onChange={(e) => setForm({ ...form, is_public: e.target.value === "public" })}
              data-testid="pm-create-visibility" className="in">
              <option value="public">Public (discoverable in panel)</option>
              <option value="targeted">Targeted (link-only)</option>
            </select>
          </F>
          <F label="Target N"><input type="number" data-testid="pm-create-target" value={form.target_n} onChange={set("target_n")} className="in" /></F>
          <F label="Reward points"><input type="number" data-testid="pm-create-reward" value={form.reward_points} onChange={set("reward_points")} className="in" /></F>
          <F label="LOI (min)"><input type="number" data-testid="pm-create-loi" value={form.loi} onChange={set("loi")} className="in" /></F>
          <F label="Incidence %"><input type="number" data-testid="pm-create-ir" value={form.incidence_rate} onChange={set("incidence_rate")} className="in" /></F>
          <F label="Survey URL (optional — leave blank to give client redirect URLs first)" wide>
            <input
              data-testid="pm-create-survey-url"
              placeholder="Leave blank now — add after client configures their survey"
              value={form.survey_url} onChange={set("survey_url")}
              className="in font-mono"
            />
          </F>
        </div>
        <div className="mt-3 rounded-lg p-3 bg-slate-500/10 border border-slate-500/25 flex items-start gap-2 text-xs text-slate-200" data-testid="pm-create-hint">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            <b>Redirect-first workflow:</b> if you leave the survey URL blank, we'll generate the redirect URLs for you to share with your client. They configure their survey platform, send you the survey link, and you paste it here (or on the project detail page) to go live.
          </span>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={() => nav("/pm")} data-testid="pm-create-cancel"
            className="px-4 py-2 rounded-md text-sm bg-white/5 hover:bg-white/10 border border-white/10">Cancel</button>
          <button type="submit" disabled={saving} data-testid="pm-create-submit"
            className="px-4 py-2 rounded-md text-sm bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60">
            {saving ? "Creating…" : (form.survey_url.trim() ? "Create & go live" : "Create draft & generate redirects")}
          </button>
        </div>
      </form>
      <style>{`.in{width:100%;background:rgba(0,0,0,0.5);border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:8px 12px;font-size:14px;color:#fff}
.in:focus{outline:none;border-color:#6366f1;box-shadow:0 0 0 1px #6366f1}`}</style>
    </Shell>
  );
}

function F({ label, wide, children }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">{label}</label>
      {children}
    </div>
  );
}
